import { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import {
  atualizarStatusCessao,
  listarDadosConsolidadosTitulos,
  listarDadosHistorico,
  listarDadosTitulosCedidos,
  listarDetalhesCessao,
  obterArquivoRemessaCessao
} from 'api/capitalFlow';
import { StatusCessaoEnum } from 'components/Shared/Enums/StatusCessaoEnum';
import { formatarDocumento } from 'components/Utils/documentoAlfanumerico';
import { baixarArquivoBlob } from 'components/Utils/utils';

const useDetalhesCessaoHook = ({
  dadosDetalhes,
  setDadosDetalhes,
  setMostrarDetalhes,
  atualizarGridHistoricoCessao
}) => {
  const [mostrarModalEnvioDocumentos, setMostrarModalEnvioDocumentos] = useState(false);
  const [mostrarModalCancelarOperacao, setMostrarModalCancelarOperacao] = useState(false);
  const [mostrarModalAssinarOperacao, setMostrarModalAssinarOperacao] = useState(false);
  const [loadingConsolidadosTitulos, setLoadingConsolidadosTitulos] = useState(false);
  const [loadingBtnAlterarStatus, setLoadingBtnAlterarStatus] = useState(false);
  const [loadingLinkRemessaCessao, setLoadingLinkRemessaCessao] = useState(false);
  const [dadosConsolidadosTitulos, setDadosConsolidadosTitulos] = useState([]);
  const [loadingResumoCessao, setLoadingResumoCessao] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [dadosResumoCessao, setDadosResumoCessao] = useState([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState({});
  const [contadorFiltros, setContadorFiltros] = useState(0);
  const [dadosHistorico, setDadosHistorico] = useState([]);
  const [filtrosAtuais, setFiltrosAtuais] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState([]);
  const statusMostraExportacao = [];
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  useEffect(() => {
    if (Object.keys(dadosDetalhes).length > 0) {
      buscaDadosAtualizadosTela();
    }
  }, [dadosDetalhes]);

  const buscaDadosAtualizadosTela = (atualizaGridhistorico = false) => {
    buscarDadosHistorico();
    buscarDadosResumoOperacao();
    buscarDadosConsolidadosTitulos();
    carregarTitulos(paginationModel, {});

    if (atualizaGridhistorico) {
      atualizarGridHistoricoCessao();
    }
  };

  const carregarTitulos = (model = paginationModel, novosFiltros = null, newSortModel = sortModel) => {
    const filtrosBase = novosFiltros ?? filtrosAtuais;
    const filtrosLimpos = cleanFilters(filtrosBase);
    const listaAtual = rows;
    const sort = newSortModel?.[0];

    setRows([]);
    setFiltrosAtuais(filtrosLimpos);

    const { page, pageSize } = model;
    const params = {
      ...filtrosLimpos,
      data_solicitacao_de: formatDateUS(filtrosLimpos.data_solicitacao_de),
      data_solicitacao_ate: formatDateUS(filtrosLimpos.data_solicitacao_ate),
      page: page,
      per_page: pageSize,
      order_by: sort?.sort ?? null,
      column_filter: sort?.field ?? null
    };

    setLoading(true);
    listarDadosTitulosCedidos(dadosDetalhes.id, params)
      .then(response => {
        const { data } = response.data;
        setRows(data ?? []);
        setRowCount(response.data.meta.total);
      })
      .catch(() => {
        setRows(listaAtual);
        openSnackbarMain('Ocorreu um erro ao buscar os títulos cedidos', 'error');
      })
      .finally(() => setLoading(false));
  };

  const formatDateUS = value =>
    value ? moment(value, ['DD/MM/YYYY', moment.ISO_8601], true).format('YYYY-MM-DD') : null;

  const cleanFilters = (obj = {}) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => !isBlank(v)));

  const isBlank = v =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

  const voltarTelaGestaoCessao = () => {
    setMostrarDetalhes(false);
    setDadosDetalhes([]);
  };

  const buscarDadosHistorico = () => {
    setLoadingHistorico(true);
    listarDadosHistorico(dadosDetalhes.id)
      .then(response => {
        const { data } = response.data;
        setDadosHistorico(data ?? []);
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao buscar os dados do histórico', 'error');
      })
      .finally(() => {
        setLoadingHistorico(false);
      });
  };

  const buscarDadosConsolidadosTitulos = () => {
    setLoadingConsolidadosTitulos(true);
    listarDadosConsolidadosTitulos(dadosDetalhes.id)
      .then(response => {
        const { data } = response;
        setDadosConsolidadosTitulos(data[0] ?? []);
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao buscar os dados do consolidado', 'error');
      })
      .finally(() => {
        setLoadingConsolidadosTitulos(false);
      });
  };

  const buscarDadosResumoOperacao = () => {
    setLoadingResumoCessao(true);
    listarDetalhesCessao(dadosDetalhes.id)
      .then(response => {
        const { data } = response.data;

        const dadosResumo = {
          status: getStatusKeyByValue(data?.CODCESSOESCAPITALFLOWSTATUS),
          qtdAprovados: data?.QTDETITULOSAPROVADOS || 0,
          codStatus: data?.CODCESSOESCAPITALFLOWSTATUS,
          qtdEnviados: data?.QTDETITULOSENVIADOS || 0,
          valorReceber: data?.VALORARECEBER || '0,00',
          descricaoStatus: data?.DESCRICAOSTATUS,
          valorTotal: data?.VALORTOTAL || '0,00',
          intencao: data?.OBSERVACOES || '- -',
          dataSolicitacao: data?.DTHRINSERT,
          dataAnalise: data?.DATAANALISE,
          taxa: data?.FATOR,
          id: data?.UUID,
          ted: data?.TED,
          existeRemessa: data?.EXISTEREMESSA ? true : false,
          exibirBotaoDownload: data?.EXIBIR_BOTAO_DOWNLOAD ? true : false
        };

        setDadosResumoCessao(dadosResumo);
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao buscar o resumo da cessão', 'error');
      })
      .finally(() => {
        setLoadingResumoCessao(false);
      });
  };

  const getStatusKeyByValue = value => {
    const n = Number(value);
    return (
      Object.keys(StatusCessaoEnum)
        .find(key => StatusCessaoEnum[key] === n)
        .toLowerCase() ?? ''
    );
  };

  const formataValor = valor =>
    parseFloat(valor).toLocaleString('pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const columns = [
    {
      field: 'NOMECEDENTE',
      headerName: 'Cedente',
      flex: 1.5,
      minWidth: 180,
      renderCell: params => (
        <div className="ajuste-nome-cedente-sacado" title={params.row.NOMECEDENTE}>
          <span>{formatarDocumento(params.row.CPFCNPJCEDENTE)}</span>
          <span>{params.row.NOMECEDENTE}</span>
        </div>
      )
    },
    {
      field: 'NOMESACADO',
      headerName: 'Sacado',
      flex: 1.5,
      minWidth: 180,
      renderCell: params => (
        <div className="ajuste-nome-cedente-sacado" title={params.row.NOMESACADO}>
          <span>{formatarDocumento(params.row.CPFCNPJSACADO)}</span>
          <span title={params.row.NOMESACADO}>{params.row.NOMESACADO}</span>
        </div>
      )
    },
    { field: 'NBORDERO', headerName: 'Borderô', flex: 0.8, minWidth: 80 },
    { field: 'NF', headerName: 'NF', flex: 0.8, minWidth: 100 },
    { field: 'NDOC', headerName: 'Nº Doc.', flex: 0.8, minWidth: 100 },
    { field: 'TDOC', headerName: 'Tipo Doc.', flex: 0.8, minWidth: 80 },
    { field: 'SIGLA', headerName: 'Tipo Op.', flex: 0.8, minWidth: 80 },
    {
      field: 'VENCIMENTO',
      headerName: 'Vencimento',
      flex: 1.0,
      minWidth: 120,
      valueFormatter: ({ value }) => fmtBRDate(value)
    },
    {
      field: 'VALOR',
      headerName: 'Valor (R$)',
      flex: 1.0,
      minWidth: 120,
      valueFormatter: ({ value }) => formataValor(value ?? 0)
    },
    {
      field: 'CODCESSOESCAPITALFLOWSTATUS',
      headerName: 'Status',
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => (
        <span
          className={`badge-cessao color-${getStatusKeyByValue(row.CODCESSOESCAPITALFLOWSTATUS)}`}
          title={row.OBSERVACOES}
        >
          {row.DESCRICAOSTATUS}
        </span>
      )
    }
  ];

  const fmtBRDate = dateString => moment(dateString, 'YYYY-MM-DD').format('DD/MM/YYYY');

  const formatarHistorico = dateTimeString => {
    const partes = dateTimeString.split(' ');
    const data = partes[0];
    const tempo = partes[1];
    const [hora, minuto] = tempo.split(':');
    const horaFormatada = `${hora}h${minuto}`;
    const [ano, mes, dia] = data.split('-');
    const dataFormatada = `${dia}/${mes}/${ano}`;
    return { hora: horaFormatada, data: dataFormatada };
  };

  const enviarDocumentos = dados => {
    setMostrarModalEnvioDocumentos(true);
  };

  const cancelarOperacao = () => {
    setMostrarModalCancelarOperacao(true);
  };

  const assinarOperacao = () => {
    setMostrarModalAssinarOperacao(true);
  };

  const toolbarTabela = () => (
    <GridToolbarContainer className="tabela-toolbar">
      <div className="toolbar-spacer" />
      <GridToolbarQuickFilter className="search-grid" placeholder="Procurar..." debounceMs={500} />
    </GridToolbarContainer>
  );

  const rowSelectionModelFiltered = rows
    .map(row => String(row.id))
    .filter(id => selectedRowsMap[id] !== undefined);

  const handleRowSelectionModelChange = newRowSelectionModel => {
    const consistentIds = newRowSelectionModel.map(String);
    const currentPageRowIds = rows.map(row => String(row.id));
    const newSelectedRowsMap = { ...selectedRowsMap };

    for (const rowId of currentPageRowIds) {
      const isRowSelected = consistentIds.includes(rowId);
      const currentRow = rows.find(row => String(row.id) === rowId);

      if (isRowSelected) {
        newSelectedRowsMap[rowId] = currentRow;
      } else {
        delete newSelectedRowsMap[rowId];
      }
    }

    setSelectedRowsMap(newSelectedRowsMap);
  };

  const handleFiltrar = filtros => {
    const filtrosLimpos = cleanFilters(filtros);
    const newModel = {
      ...paginationModel,
      page: 0
    };

    setPaginationModel(newModel);
    setContadorFiltros(verificaContaFiltros(filtros));
    carregarTitulos(newModel, filtrosLimpos);
  };

  const verificaContaFiltros = filtros => {
    const on = v => (typeof v === 'string' ? v.trim() !== '' : !!v);
    let n = 0;
    if (on(filtros.nome_cedente)) n++;
    if (on(filtros.cpfcnpj_cedente)) n++;
    if (on(filtros.nome_sacado)) n++;
    if (on(filtros.cpfcnpj_sacado)) n++;
    if (on(filtros.tipo_operacao)) n++;
    if (on(filtros.tipo_documento)) n++;
    if (on(filtros.numero_documento)) n++;
    if (on(filtros.bordero)) n++;
    if (filtros.data_aceite_de || filtros.data_aceite_ate) n++;
    if (filtros.venc_ate || filtros.venc_de) n++;
    return n;
  };

  const handleSortModelChange = model => {
    setSortModel(model);
    carregarTitulos(paginationModel, filtrosAtuais, model);
  };

  const handlePaginationModelChange = model => {
    setPaginationModel(model);
    carregarTitulos(model, filtrosAtuais, sortModel);
  };

  const alterarStatusCessao = codStatusCessao => {
    setLoadingBtnAlterarStatus(true);

    const params = {
      uuid_cessao: dadosDetalhes.id,
      cod_status_cessao: codStatusCessao
    };

    atualizarStatusCessao(params)
      .then(() => {
        buscaDadosAtualizadosTela(true);
      })
      .catch(() => {
        openSnackbarMain(
          `Ocorreu um erro ao ${codStatusCessao === StatusCessaoEnum.INDEFERIDO ? 'cancelar' : 'aceitar'} a cessão`,
          'error'
        );
      })
      .finally(() => {
        setLoadingBtnAlterarStatus(false);
        setMostrarModalCancelarOperacao(false);
        setMostrarModalAssinarOperacao(false);
      });
  };

  const downloadArquivoRemessaCessao = () => {
    setLoadingLinkRemessaCessao(true);

    obterArquivoRemessaCessao(dadosDetalhes.id)
      .then(res => {
        if (res?.data?.data?.content) {
          const conteudoArquivo = res?.data?.data?.content;
          const mimetypeArquivo = res?.data?.data?.mime_type;
          const nomeArquivo = res?.data?.data?.file_name;
          baixarArquivoBlob(nomeArquivo, conteudoArquivo, mimetypeArquivo);
        } else {
          openSnackbarMain('Arquivo da remessa não encontrado', 'warning');
        }
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao buscar o arquivo da remessa da cessão', 'error');
      })
      .finally(() => {
        setLoadingLinkRemessaCessao(false);
      });
  };

  return {
    setMostrarModalCancelarOperacao,
    setMostrarModalEnvioDocumentos,
    setMostrarModalAssinarOperacao,
    handleRowSelectionModelChange,
    downloadArquivoRemessaCessao,
    mostrarModalCancelarOperacao,
    mostrarModalAssinarOperacao,
    handlePaginationModelChange,
    mostrarModalEnvioDocumentos,
    loadingConsolidadosTitulos,
    rowSelectionModelFiltered,
    buscaDadosAtualizadosTela,
    dadosConsolidadosTitulos,
    loadingLinkRemessaCessao,
    loadingBtnAlterarStatus,
    voltarTelaGestaoCessao,
    statusMostraExportacao,
    handleSortModelChange,
    loadingResumoCessao,
    alterarStatusCessao,
    dadosResumoCessao,
    formatarHistorico,
    loadingHistorico,
    cancelarOperacao,
    enviarDocumentos,
    paginationModel,
    contadorFiltros,
    assinarOperacao,
    dadosHistorico,
    handleFiltrar,
    toolbarTabela,
    formataValor,
    sortModel,
    rowCount,
    loading,
    columns,
    rows
  };
};

export default useDetalhesCessaoHook;
